import { currentDayRange, isCurrentDayRange } from '../dateTime/historyDateTime.js'

export const naftaPosAuditService = 'naftapos'
const cacheFormat = 'naftapos-journals-v3'
export const auditCategories = [['auth', 'categoryAuth'], ['access', 'categoryAccess'], ['users', 'categoryUsers'],
  ['stations', 'categoryStations'], ['reports', 'categoryReports'], ['settings', 'categorySettings'], ['catalog', 'categoryCatalog']]
export const outcomes = ['RUNNING', 'SUCCESS', 'NO_DATA', 'FAILED', 'INTERRUPTED']
export const errorCategories = ['MISSING_REQUIRED_DATA', 'DATABASE_CONNECTION', 'DATABASE_QUERY', 'REPORT_PARAMETERS',
  'TEMPLATE_RENDERING', 'TIMEOUT', 'SERVICE_RESOURCES', 'ACCESS', 'UNKNOWN']
export const runKinds = ['report_pdf', 'report_xlsx', 'report_html', 'chart_json', 'chart_jpeg', 'chart_pdf', 'report_parameters', 'chart_parameters']
const fields = { runs: ['q', 'kind', 'templateId', 'outcome', 'errorCategory', 'actor', 'sourceInstanceId', 'fromTime', 'toTime'],
  audit: ['q', 'category', 'outcome', 'errorCategory', 'actor', 'sourceInstanceId', 'fromTime', 'toTime'] }
const runFields = ['runId', 'correlationId', 'jobId', 'actorUserId', 'actor', 'stationId', 'stationName', 'sourceInstanceId',
  'sourceName', 'templateId', 'templateName', 'kind', 'outcome', 'errorCode', 'errorCategory', 'startedAt', 'completedAt',
  'durationMs', 'resultBytes', 'heapUsedBeforeBytes', 'heapUsedAfterBytes', 'heapDeltaBytes', 'diagnosticAvailability',
  'diagnosticExpiresAt', 'diagnosticExpiredAt', 'diagnosticExpirationReason', 'expiresAt']
const reportPayloadFields = ['schemaVersion', 'runId', 'sourceInstanceId', 'outcome', 'errorCode', 'errorCategory',
  'diagnosticAvailability', 'diagnosticExpiresAt', 'diagnosticExpiredAt', 'diagnosticExpirationReason']
const accessEvents = new Set(['access_user_created', 'access_user_updated', 'access_user_blocked', 'access_user_unblocked',
  'access_user_deleted', 'access_role_created', 'access_role_updated', 'access_role_deleted'])
const accessRoles = new Set(['ADMIN', 'MANAGER', 'SERVICE_SPECIALIST', 'OPERATOR'])
const accessStatuses = new Set(['ACTIVE', 'BLOCKED', 'PENDING_PASSWORD_CHANGE', 'DELETED'])
const scalar = value => value === null || ['string', 'number', 'boolean'].includes(typeof value)
const pick = (value, names) => Object.fromEntries(names.filter(name => Object.hasOwn(value ?? {}, name) && scalar(value[name])).map(name => [name, value[name]]))
const clone = value => JSON.parse(JSON.stringify(value))
const pad = value => String(value).padStart(2, '0')

export function defaultFilters(journal, now = new Date(), actor = '') {
  const day = currentDayRange(now, { utc: true })
  return Object.fromEntries(fields[journal].map(name => [name, name === 'fromTime' ? day.from
    : name === 'toTime' ? day.to : name === 'actor' ? actor : '']))
}
export function utcFields(value) {
  if (!value) return { date: '', time: '' }
  const date = new Date(value)
  return { date: `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}.${date.getUTCFullYear()}`,
    time: `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}` }
}
export function parseUtcFields(value) {
  if (!value?.date && !value?.time) return ''
  const date = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value?.date ?? '')
  const time = /^(\d{2}):(\d{2}):(\d{2})$/.exec(value?.time ?? '')
  if (!date || !time) throw new Error('journalInvalidDateTime')
  const [, day, month, year] = date.map(Number), [, hour, minute, second] = time.map(Number)
  const parsed = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
  if (year < 1000 || parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day
    || parsed.getUTCHours() !== hour || parsed.getUTCMinutes() !== minute || parsed.getUTCSeconds() !== second) throw new Error('journalInvalidDateTime')
  return parsed.toISOString()
}
export function toDraft(applied) {
  const result = { ...applied, from: utcFields(applied.fromTime),
    to: utcFields(applied.toTime ? new Date(Date.parse(applied.toTime) - 1000).toISOString() : '') }
  delete result.fromTime; delete result.toTime
  return result
}
export function prepareFilters(journal, draft) {
  const result = pick(draft, fields[journal])
  result.fromTime = parseUtcFields(draft.from)
  const end = parseUtcFields(draft.to)
  if (!result.fromTime || !end) throw new Error('journalInvalidDateTime')
  result.toTime = new Date(Date.parse(end) + 1000).toISOString()
  if (result.fromTime && result.toTime && Date.parse(result.fromTime) >= Date.parse(result.toTime)) throw new Error('journalInvalidRange')
  if (result.errorCategory && result.outcome !== 'FAILED') throw new Error('journalInvalidErrorFilter')
  if ((result.q?.length ?? 0) > 120 || (result.actor?.length ?? 0) > 64 || (result.templateId?.length ?? 0) > 128) throw new Error('journalInvalidFilter')
  return result
}
export function journalPeriodDescription(applied, now = new Date()) {
  if (isCurrentDayRange(applied.fromTime, applied.toTime, now, { utc: true })) return ''
  const draft = toDraft(applied)
  return `${draft.from.date} ${draft.from.time} – ${draft.to.date} ${draft.to.time} UTC`
}
export function setDraftOutcome(draft, value) { draft.outcome = value; if (value !== 'FAILED') draft.errorCategory = '' }
export function setDraftErrorCategory(draft, value) { draft.errorCategory = value; if (value) draft.outcome = 'FAILED' }
export function journalQueryKey(journal, { applied, page = 1, pageSize = 25 }) {
  return JSON.stringify([cacheFormat, journal, fields[journal].map(name => [name, applied[name] ?? '']), page, pageSize])
}
export function hasDraftChanges(journal, draft, applied) {
  try { return journalQueryKey(journal, { applied: prepareFilters(journal, draft) }) !== journalQueryKey(journal, { applied }) }
  catch { return true }
}
export function projectAccessAuditPayload(eventType, payload = {}) {
  if (!accessEvents.has(eventType) || !payload) return {}
  const result = {}
  for (const [names, values] of [[['oldRoleCode', 'newRoleCode'], accessRoles], [['oldStatus', 'newStatus'], accessStatuses]]) {
    for (const name of names) if (Object.hasOwn(payload, name) && (payload[name] === null || values.has(payload[name]))) result[name] = payload[name]
  }
  for (const [countName, arrayName, valid] of [
    ['addedStationCount', 'addedStationIds', value => Number.isSafeInteger(value) && value > 0],
    ['removedStationCount', 'removedStationIds', value => Number.isSafeInteger(value) && value > 0],
    ['addedPermissionCount', 'addedPermissionKeys', value => typeof value === 'string' && value.length <= 128 && /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+$/.test(value)],
    ['removedPermissionCount', 'removedPermissionKeys', value => typeof value === 'string' && value.length <= 128 && /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+$/.test(value)],
  ]) {
    const count = payload[countName], items = payload[arrayName]
    if (Object.hasOwn(payload, countName)) {
      if (Number.isSafeInteger(count) && count >= 0 && count <= 1000) result[countName] = count
    } else if (Array.isArray(items) && items.length <= 1000 && items.every(valid)) result[countName] = items.length
  }
  return result
}
export function projectJournalPage(journal, value) {
  if (journal === 'audit' && value?.service !== naftaPosAuditService) {
    throw Object.assign(new Error('Audit service could not be confirmed'), { status: 403, code: 'monitoring_service_unconfirmed' })
  }
  const result = pick(value, ['schemaVersion', 'service', 'page', 'pageSize', 'total'])
  if (journal === 'runs') result.runs = (value.runs ?? []).slice(0, 100).map(item => pick(item, runFields))
  else result.events = (value.events ?? []).slice(0, 100).map(item => ({ ...pick(item,
    ['id', 'stationId', 'actor', 'eventType', 'subjectType', 'subjectId', 'subjectName', 'correlationId', 'createdAt']),
  payload: accessEvents.has(item.eventType) ? projectAccessAuditPayload(item.eventType, item.payload) : pick(item.payload, reportPayloadFields) }))
  return result
}
export function journalScope(session) {
  if (!session.user) return ''
  const context = session.platformContext
  const station = context?.stations?.find(item => Number(item.id) === Number(context.activeStationId))
  return JSON.stringify([cacheFormat, session.user.id ?? session.user.username, session.user.accessVersion,
    session.user.role?.systemCode ?? session.user.role, [...(session.user.effectivePermissions ?? [])].sort(),
    context?.activeStationId, context?.activeNaftaPosInstanceId, session.user.reportLanguage ?? 'uk', station?.timezone ?? null])
}
export function createJournalCache({ now = () => Date.now(), staleTime = 60_000 } = {}) {
  let scope = '', epoch = 0
  const entries = new Map()
  const ticket = () => ({ scope, epoch })
  const current = owner => Boolean(scope) && owner?.scope === scope && owner?.epoch === epoch
  const clear = () => { entries.clear(); epoch++ }
  const setScope = value => { if (scope !== value) { clear(); scope = value } }
  function savePreferences(journal, owner, value) {
    if (!fields[journal] || !current(owner)) return false
    const preferences = { applied: pick(value.applied, fields[journal]), draft: { ...pick(value.draft, fields[journal]),
      from: pick(value.draft.from, ['date', 'time']), to: pick(value.draft.to, ['date', 'time']) },
    searchDraft: String(value.searchDraft ?? '').slice(0, 120), page: value.page, pageSize: value.pageSize }
    entries.set(journal, { ...entries.get(journal), preferences }); return true
  }
  function saveResult(journal, owner, query, value) {
    if (!fields[journal] || !current(owner)) return false
    entries.set(journal, { ...entries.get(journal), result: { key: journalQueryKey(journal, query), page: projectJournalPage(journal, value), fetchedAt: now() } }); return true
  }
  function restore(journal, owner) {
    const entry = entries.get(journal)
    if (!current(owner) || !entry?.preferences) return null
    const matches = entry.result?.key === journalQueryKey(journal, entry.preferences)
      && (journal !== 'audit' || entry.result.page?.service === naftaPosAuditService)
    return clone({ ...entry.preferences, result: matches ? entry.result.page : null,
      fetchedAt: matches ? entry.result.fetchedAt : null, fresh: matches && now() - entry.result.fetchedAt < staleTime })
  }
  return { ticket, current, clear, setScope, savePreferences, saveResult, restore }
}
